import { useTheme } from 'providers/ThemeProvider';
import React, { useRef } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { ChartOptions, TooltipItem } from 'chart.js';
import { colorPalette } from 'utils/styleConstants';

type Value = { x: string; y: number; formattedY: string; percentage?: number };

export interface PieChartProps {
  data: Value[];
  colors?: string[];
}

interface CustomDataset {
  data: number[];
  percentages: (number | undefined)[];
  formattedY: string[];
  backgroundColor?: string[];
  borderColor?: string;
  borderWidth?: number;
}

const PieChart = ({ data: dataValues, colors }: PieChartProps) => {
  const chartRef = useRef<any>(null);
  const { theme } = useTheme();

  const data = {
    labels: dataValues.map((item) => item.x),
    datasets: [
      {
        data: dataValues.map((item) => item.y),
        percentages: dataValues.map((item) => item.percentage),
        formattedY: dataValues.map((item) => item.formattedY),
        backgroundColor: colors,
        borderColor: 'white',
        borderWidth: 1,
      },
    ],
  };

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'left',
        align: 'start',
        labels: {
          boxWidth: 20,
          boxHeight: 10,
          padding: 15,
          font: {
            size: 13,
            family: theme.fontFamily,
            weight: 'lighter',
          },
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems: TooltipItem<'doughnut'>[]) => tooltipItems[0].label,
          label: (tooltipItem: TooltipItem<'doughnut'>) => {
            const dataset = tooltipItem.dataset as unknown as CustomDataset;
            const value = dataset.formattedY[tooltipItem.dataIndex];
            const percentage = dataset.percentages[tooltipItem.dataIndex];
            return `${value} (${percentage}%)`;
          },
        },
        backgroundColor: colorPalette.white,
        titleColor: colorPalette.turquoise.dark,
        bodyColor: colorPalette.turquoise.dark,
        displayColors: true,
        boxWidth: 15,
        boxHeight: 15,
        padding: 6,
        usePointStyle: false,
        titleFont: {
          weight: '400',
        },
        bodyFont: {
          size: 12,
          weight: '400',
        },
        caretPadding: 10,
        borderColor: colorPalette.grey.grey3,
        borderWidth: 1,
      },
    },
  };

  return <Doughnut ref={chartRef} data={data} options={options} />;
};

export default PieChart;
